import React, { useState, useRef } from 'react';
import { formConfigService } from '../../../services/form-config.service';
import { getErrorMsg } from '../../../utils/error';

interface IconUploaderProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
}

export const IconUploader = ({ label, value, onChange }: IconUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadErr('');
    try {
      const { url } = await formConfigService.uploadIcon(file);
      onChange(url);
    } catch (err: unknown) {
      setUploadErr(getErrorMsg(err, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
        {label}
        {value && (
          <span className="ml-2 inline-flex items-center gap-1">
            <img src={value} alt="" className="w-4 h-4 object-contain rounded" />
            <span className="text-gray-300 text-xs">preview</span>
          </span>
        )}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          placeholder="https://… or upload →"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="shrink-0 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          title="Upload image to S3"
        >
          {uploading ? '…' : '↑ Upload'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {uploadErr && <p className="mt-1 text-xs text-red-500">{uploadErr}</p>}
    </div>
  );
};
